import { IconArrowUp } from "@/assets/icons/geist/IconArrowUp";
import { useReviewComments, formatReviewCommentsForAgent } from "@/components/ai/review/use-review-comments";
import type { PendingCommentSelection } from "@/components/ai/review/types";
import { Button } from "@/components/flexkit/Button";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogBody,
} from "@/components/podkit/dialog/Dialog";
import { useEnvironmentConversation, getAgentExecution } from "@/hooks/use-environment-conversation";
import { useOptimisticPendingMessages } from "@/hooks/use-pending-messages";
import { useStreamConversation } from "@/hooks/use-stream-conversation";
import { useSendToAgentExecution } from "@/queries/agent-queries";
import { useEnvironment } from "@/queries/environment-queries";
import { CodeChangesPanel, type CodeChangesPanelRef } from "@/routes/environments/details/CodeChangesPanel";
import { GitStatusPanelCompact } from "@/routes/environments/details/GitStatusPanelCompact";
import { ReviewCommentsSection } from "@/routes/environments/details/ReviewCommentsSection";
import {
    AgentExecution_Phase,
    SendToAgentExecutionRequest,
    UserInputBlock,
    UserInputBlock_Input,
    UserInputBlock_TextInput,
} from "gitpod-next-api/gitpod/v1/agent_pb";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";

export type EnvironmentChangesViewRef = {
    scrollToFile: (filePath: string) => void;
    openFile: (filePath: string) => void;
};

export type EnvironmentChangesViewProps = {
    environmentId: string;
    enableReviewComments?: boolean;
};

export const EnvironmentChangesView = forwardRef<EnvironmentChangesViewRef, EnvironmentChangesViewProps>(
    ({ environmentId, enableReviewComments = false }, ref) => {
        const codeChangesPanelRef = useRef<CodeChangesPanelRef>(null);
        const { data: environment } = useEnvironment(environmentId);
        const { environmentConversation } = useEnvironmentConversation(environmentId);
        const agentExecution = getAgentExecution(environmentConversation);
        const sendToAgentExecution = useSendToAgentExecution();

        // Get conversation messages to extract code annotations
        const { pendingMessages } = useOptimisticPendingMessages();
        const { messages } = useStreamConversation(
            agentExecution?.id,
            agentExecution?.status?.conversationUrl,
            pendingMessages,
        );

        const {
            comments,
            updateComment,
            removeComment,
            clearAllComments,
            dismissReviewTab,
            addComment,
            updateCodeContext,
            isAnnotationDismissed,
        } = useReviewComments(environmentId);

        // Track processed annotations to avoid re-running effect when comments change
        const processedAnnotationsRef = useRef<Set<string>>(new Set());

        // Convert agent code annotations to review comments (only when feature flag is enabled)
        useEffect(() => {
            if (!enableReviewComments || !messages || !environmentId) return;

            for (const message of messages) {
                if (message.type !== "agent") continue;
                for (const fragment of message.fragments) {
                    if (fragment.type === "codeAnnotation") {
                        const annotation = fragment;

                        // Skip if already processed in this session
                        if (processedAnnotationsRef.current.has(annotation.id)) continue;

                        // Skip if this annotation was previously dismissed by the user
                        if (isAnnotationDismissed(annotation.id)) {
                            processedAnnotationsRef.current.add(annotation.id);
                            continue;
                        }

                        // Mark as processed before adding to prevent re-processing
                        processedAnnotationsRef.current.add(annotation.id);
                        addComment(
                            annotation.filePath,
                            annotation.startLine,
                            annotation.endLine,
                            annotation.description,
                            "agent",
                            annotation.id,
                        );
                    }
                }
            }
        }, [enableReviewComments, messages, environmentId, addComment, isAnnotationDismissed]);

        const [pendingSelection, setPendingSelection] = useState<PendingCommentSelection | null>(null);
        const [isSubmitting, setIsSubmitting] = useState(false);
        const [showSubmitDialog, setShowSubmitDialog] = useState(false);
        const [selectedCommentIds, setSelectedCommentIds] = useState<Set<string>>(new Set());
        const [additionalContext, setAdditionalContext] = useState("");
        const [isReviewLoading, setIsReviewLoading] = useState(false);
        const [isCreatePRLoading, setIsCreatePRLoading] = useState(false);

        const gitStatus = environment?.status?.content?.git;

        useImperativeHandle(
            ref,
            () => ({
                scrollToFile: (filePath: string) => {
                    codeChangesPanelRef.current?.scrollToFile(filePath);
                },
                openFile: (filePath: string) => {
                    codeChangesPanelRef.current?.openFile(filePath);
                },
            }),
            [],
        );

        const selectedComments = useMemo(() => {
            if (selectedCommentIds.size === 0) {
                return comments;
            }
            return comments.filter((c) => selectedCommentIds.has(c.id));
        }, [comments, selectedCommentIds]);

        const handleSubmitReview = useCallback(async () => {
            if (!agentExecution?.id || selectedComments.length === 0) return;

            setIsSubmitting(true);
            try {
                const reviewText = formatReviewCommentsForAgent(selectedComments, additionalContext.trim() || undefined);

                await sendToAgentExecution.mutateAsync(
                    new SendToAgentExecutionRequest({
                        agentExecutionId: agentExecution.id,
                        userInput: new UserInputBlock({
                            id: uuidv4(),
                            inputs: [
                                new UserInputBlock_Input({
                                    input: {
                                        case: "text",
                                        value: new UserInputBlock_TextInput({
                                            content: reviewText,
                                        }),
                                    },
                                }),
                            ],
                        }),
                    }),
                );

                // Clear submitted comments
                for (const comment of selectedComments) {
                    removeComment(comment.id);
                }
                setSelectedCommentIds(new Set());
                setShowSubmitDialog(false);
                setAdditionalContext("");
            } catch (error) {
                console.error("Failed to submit review:", error);
            } finally {
                setIsSubmitting(false);
            }
        }, [agentExecution?.id, selectedComments, additionalContext, sendToAgentExecution, removeComment]);

        const handleDismissReview = useCallback(() => {
            clearAllComments();
            dismissReviewTab();
        }, [clearAllComments, dismissReviewTab]);

        const handleAddComment = useCallback(
            (filePath: string, startLine: number, endLine: number, content: string) => {
                addComment(filePath, startLine, endLine, content, "user");
                setPendingSelection(null);
            },
            [addComment],
        );

        const handleLineSelectionForComment = useCallback((selection: PendingCommentSelection) => {
            setPendingSelection(selection);
        }, []);

        const handleStartReview = useCallback(async () => {
            if (!agentExecution?.id || !gitStatus?.changedFiles.length) return;

            setIsReviewLoading(true);
            try {
                const fileList = gitStatus.changedFiles.map((f) => `- ${f.path}`).join("\n");
                const reviewCommand = `/ona:review\n\nFiles changed:\n${fileList}`;

                await sendToAgentExecution.mutateAsync(
                    new SendToAgentExecutionRequest({
                        agentExecutionId: agentExecution.id,
                        userInput: new UserInputBlock({
                            id: uuidv4(),
                            inputs: [
                                new UserInputBlock_Input({
                                    input: {
                                        case: "text",
                                        value: new UserInputBlock_TextInput({
                                            content: reviewCommand,
                                        }),
                                    },
                                }),
                            ],
                        }),
                    }),
                );
            } catch (error) {
                console.error("Failed to start review:", error);
            } finally {
                setIsReviewLoading(false);
            }
        }, [agentExecution?.id, gitStatus?.changedFiles, sendToAgentExecution]);

        const handleCreatePullRequest = useCallback(async () => {
            if (!agentExecution?.id) return;

            setIsCreatePRLoading(true);
            try {
                await sendToAgentExecution.mutateAsync(
                    new SendToAgentExecutionRequest({
                        agentExecutionId: agentExecution.id,
                        userInput: new UserInputBlock({
                            id: uuidv4(),
                            inputs: [
                                new UserInputBlock_Input({
                                    input: {
                                        case: "text",
                                        value: new UserInputBlock_TextInput({
                                            content: "Create a pull request for the current changes.",
                                        }),
                                    },
                                }),
                            ],
                        }),
                    }),
                );
            } catch (error) {
                console.error("Failed to create pull request:", error);
            } finally {
                setIsCreatePRLoading(false);
            }
        }, [agentExecution?.id, sendToAgentExecution]);

        const isAgentBusy =
            agentExecution?.status?.phase === AgentExecution_Phase.RUNNING ||
            agentExecution?.status?.phase === AgentExecution_Phase.PENDING;

        const submitButtonText = useMemo(() => {
            if (isSubmitting) return "Submitting...";
            if (selectedCommentIds.size === 0) return `Submit all (${comments.length})`;
            return `Submit selected (${selectedCommentIds.size})`;
        }, [isSubmitting, selectedCommentIds.size, comments.length]);

        return (
            <div className={"flex grow flex-col gap-4 @container"} data-testid="environment-changes-view">
                <GitStatusPanelCompact environmentId={environmentId} />

                {/* Review Comments Section - only shown when flag enabled and there are comments */}
                {enableReviewComments && comments.length > 0 && environment && (
                    <>
                        <ReviewCommentsSection
                            environmentId={environmentId}
                            comments={comments}
                            selectedCommentIds={selectedCommentIds}
                            onToggleSelect={(commentId) => {
                                setSelectedCommentIds((prev) => {
                                    const next = new Set(prev);
                                    if (next.has(commentId)) {
                                        next.delete(commentId);
                                    } else {
                                        next.add(commentId);
                                    }
                                    return next;
                                });
                            }}
                            onEditComment={updateComment}
                            onRemoveComment={removeComment}
                            onUpdateCodeContext={updateCodeContext}
                            onSubmitClick={() => setShowSubmitDialog(true)}
                            onDismiss={handleDismissReview}
                            submitButtonText={submitButtonText}
                            isSubmitting={isSubmitting}
                        />

                        {/* Submit Review Dialog */}
                        <Dialog open={showSubmitDialog} onOpenChange={setShowSubmitDialog}>
                            <DialogContent data-track-location="review_submit_dialog">
                                <DialogHeader>
                                    <DialogTitle>Submit review</DialogTitle>
                                </DialogHeader>
                                <DialogBody>
                                    <textarea
                                        value={additionalContext}
                                        onChange={(e) => setAdditionalContext(e.target.value)}
                                        placeholder="Add additional context (optional)..."
                                        className="min-h-[100px] w-full resize-none rounded-md border border-border-base bg-surface-primary p-3 text-sm text-content-primary placeholder:text-content-muted focus:border-border-strong focus:outline-none"
                                        rows={4}
                                        autoFocus
                                    />
                                </DialogBody>
                                <DialogFooter>
                                    <Button
                                        variant="secondary"
                                        onClick={() => setShowSubmitDialog(false)}
                                        disabled={isSubmitting}
                                        data-tracking-id="cancel"
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        onClick={handleSubmitReview}
                                        disabled={isSubmitting || selectedComments.length === 0 || isAgentBusy}
                                        LeadingIcon={IconArrowUp}
                                        data-tracking-id="confirmsubmit"
                                    >
                                        {isSubmitting ? "Submitting..." : "Submit"}
                                    </Button>
                                </DialogFooter>
                            </DialogContent>
                        </Dialog>
                    </>
                )}

                <CodeChangesPanel
                    ref={codeChangesPanelRef}
                    environmentId={environmentId}
                    enableReviewComments={enableReviewComments}
                    comments={comments}
                    pendingSelection={pendingSelection}
                    onLineSelectionForComment={handleLineSelectionForComment}
                    onAddComment={handleAddComment}
                    onCancelComment={() => setPendingSelection(null)}
                    onStartReview={gitStatus?.changedFiles.length ? handleStartReview : undefined}
                    onCreatePullRequest={handleCreatePullRequest}
                    isReviewLoading={isReviewLoading}
                    isCreatePRLoading={isCreatePRLoading}
                    isCreatePRDisabled={isAgentBusy}
                />
            </div>
        );
    },
);
EnvironmentChangesView.displayName = "EnvironmentChangesView";
